"use client"

import { VideoType } from "@/app/types/video";
import { generateVideoPosterSrc } from "@v2/components/MediaV2";
import { Box, colors, Skeleton, Typography } from "@mui/material";
import dynamic from "next/dynamic";

const CustomHlsMuiPlayer = dynamic(
    () => import("@v2/components/CustomHlsMuiPlayer"),
    { ssr: false }
);
import VideoProcessingNotice from "@v2/components/VideoProcessingNotice";
import VideoReuploadRequiredNotice from "@v2/components/VideoReuploadRequiredNotice";
import { Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";

interface VideoStreamProps {
    slug: string,
    title?: string,
    ratio?: string,
    // An image media ID that replaces the video's own poster, here and inside the player.
    poster?: string | null,
    onPlayingChange?: (playing: boolean) => void,
    onSeekingChange?: (seeking: boolean) => void,
    // True while the player is showing its poster at position zero -- before the first
    // play, and again whenever the video is wound back to the start and paused.
    onStartCoverChange?: (visible: boolean) => void,
}

const fetchVideo = async (slug: string): Promise<VideoType | null> => {
    const apiBase = process.env.NEXT_PUBLIC_BASE_API_URL?.replace(/\/+$/, "");
    if (!apiBase) return null;

    const res = await fetch(`${apiBase}/api/v1/video/${slug}`);
    if (!res.ok) return null;

    const data = await res.json();
    return data?.data ?? null;
}

export default function VideoStream({ slug, title, ratio = "16 / 9", poster, onPlayingChange, onSeekingChange, onStartCoverChange }: VideoStreamProps) {
    const [readySlug, setReadySlug] = useState<string | null>(null);
    const [posterLoaded, setPosterLoaded] = useState(false);
    const { data: video, error, isLoading } = useSWR(
        slug ? ["video-stream", slug] : null,
        ([, videoSlug]) => fetchVideo(videoSlug),
        { revalidateIfStale: false, revalidateOnFocus: false }
    );

    const needsReupload = !!video && !video.media.video;
    const isProcessing = !!video && !needsReupload && video.media.video?.status !== "ready";
    const canPlayStream = !!video && !needsReupload && !isProcessing;
    const posterSrc = useMemo(() => {
        // A poster handed in wins over the record's: the caller framed it for the box it
        // is drawing, and the record's was framed for the player.
        const posterId = poster ?? video?.poster?.id ?? null;
        return posterId ? generateVideoPosterSrc(posterId) : null;
    }, [poster, video]);
    const playerReady = readySlug === slug;
    const showPlaceholder = isLoading || (canPlayStream && !playerReady);
    const onPlayerReady = useCallback(() => setReadySlug(slug), [slug]);

    useEffect(() => {
        setPosterLoaded(false);
    }, [posterSrc]);

    return (
        <Box sx={{
            position: 'relative',
            width: 1,
            aspectRatio: ratio,
            bgcolor: 'black',
            overflow: 'hidden',
        }}>
            {canPlayStream ? (
                <CustomHlsMuiPlayer
                    key={slug}
                    data={video}
                    posterId={poster}
                    embed
                    onReady={onPlayerReady}
                    onPlayingChange={onPlayingChange}
                    onSeekingChange={onSeekingChange}
                    onStartCoverChange={onStartCoverChange}
                />
            ) : isProcessing ? (
                <VideoProcessingNotice />
            ) : needsReupload ? (
                <VideoReuploadRequiredNotice />
            ) : (!isLoading || error) ? (
                <Box sx={{ width: "100%", height: "100%", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: 'dimgray', fontFeatureSettings: '"ss05"', direction: 'rtl' }}>
                        {title ?? "ویدیو یافت نشد"}
                    </Typography>
                </Box>
            ) : null}
            {showPlaceholder && (
                <Box sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 3,
                    pointerEvents: "none",
                    bgcolor: posterSrc ? 'black' : colors.grey[200],
                    opacity: playerReady ? 0 : 1,
                    transition: "opacity 220ms ease",
                }}>
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            borderRadius: 0,
                            bgcolor: colors.grey[200],
                            opacity: posterLoaded ? 0 : 1,
                            transition: "opacity 180ms ease",
                        }}
                    />
                    {posterSrc && (
                        <Box
                            component="img"
                            src={posterSrc}
                            alt={title ?? video?.title ?? ""}
                            // @ts-ignore
                            fetchPriority="high"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                            }}
                            onLoad={() => setPosterLoaded(true)}
                            onError={() => setPosterLoaded(true)}
                        />
                    )}
                    {canPlayStream && !isLoading && (
                        <Box sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <Box sx={{
                                width: { xs: 54, md: 64 },
                                height: { xs: 54, md: 64 },
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.48)",
                                backdropFilter: "blur(4px)",
                                boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
                            }}>
                                <Play size={30} color="#fff" fill="#fff" style={{ marginLeft: 3 }} />
                            </Box>
                        </Box>
                    )}
	                </Box>
	            )}
        </Box>
    )
}
